/**
 * The Inspector's layout, derived only from component schemas (rule 4): one
 * section per component in `display.order`, one row per prop. Nothing here
 * names a component, so a new component appears with no Inspector changes.
 *
 * Generic presentation rules:
 * - a `yearMonth` prop with `pairWith` renders with its partner as one date
 *   range row ("Dates: Sep 2025 → Present"), and the partner isn't listed again;
 * - optional props with no value are hidden (PropBase.optional);
 * - a prop with `shownWhen` shows only while its asset prop holds an asset of
 *   that kind (for several entities, only when each one does);
 * - an asset prop holding one of a skin set's models gets a Skin row under it,
 *   choosing among the set's skins (for several entities, only when they all
 *   hold the same model). It edits the same prop: a skin is just a model.
 *
 * With several entities selected, only the components they all have appear,
 * as in Unreal's multi-object details; an optional prop shows if any has it.
 */
import type { SkinSet } from "../../assets/content";
import type { EntityRecord } from "../../world/entity";
import type { ComponentRegistry } from "../../world/schema/ComponentRegistry";
import type { ComponentSchema } from "../../world/schema/defineComponent";
import type { PropType } from "../../world/schema/propTypes";

export type InspectorRow =
  | { readonly kind: "prop"; readonly key: string; readonly label: string; readonly prop: PropType }
  | {
      readonly kind: "range";
      readonly label: string;
      readonly from: string;
      readonly to: string;
      readonly fromProp: PropType;
      readonly toProp: PropType;
    }
  | { readonly kind: "skin"; readonly key: string; readonly label: string; readonly set: SkinSet };

export interface InspectorSection {
  /** Component type, e.g. `Role`. */
  readonly type: string;
  /** Section title: the schema's category, e.g. `Mesh Renderer`. */
  readonly title: string;
  readonly rows: readonly InspectorRow[];
}

/** What the layout needs to know about the assets props point at. */
export interface AssetLookup {
  // Function properties, not methods, so callers can take them apart.
  /** The kind of the asset at a path (`gltf`, `shape`), or null when it isn't known. */
  readonly kindOf: (path: string) => string | null;
  /** The skin set a model is one of, or null. */
  readonly skinSetOf: (path: string) => SkinSet | null;
}

/** The Skin row's label. */
const SKIN_LABEL = "Skin";

export function inspectorSections(
  entities: readonly EntityRecord[],
  components: ComponentRegistry,
  assets: AssetLookup,
): InspectorSection[] {
  if (entities.length === 0) return [];
  return components
    .all()
    .filter((schema) => entities.every((entity) => entity.components.has(schema.type)))
    .map((schema) => {
      const data = entities.map((entity) => entity.components.get(schema.type) ?? {});
      return { type: schema.type, title: schema.category, rows: rowsFor(schema, data, assets) };
    });
}

function rowsFor(
  schema: ComponentSchema,
  data: readonly Readonly<Record<string, unknown>>[],
  assets: AssetLookup,
): InspectorRow[] {
  const partners = new Set(
    Object.values(schema.props).flatMap((prop) =>
      prop.type === "yearMonth" && prop.pairWith ? [prop.pairWith] : [],
    ),
  );
  const rows: InspectorRow[] = [];

  for (const [key, prop] of Object.entries(schema.props)) {
    if (partners.has(key)) continue;
    if (prop.optional && data.every((values) => values[key] === undefined)) continue;
    if (!data.every((values) => isShown(prop, values, assets))) continue;

    const partner = prop.type === "yearMonth" ? prop.pairWith : undefined;
    const partnerProp = partner === undefined ? undefined : schema.props[partner];
    if (partner !== undefined && partnerProp) {
      rows.push({
        kind: "range",
        label: prop.label ?? key,
        from: key,
        to: partner,
        fromProp: prop,
        toProp: partnerProp,
      });
    } else {
      rows.push({ kind: "prop", key, label: prop.label ?? key, prop });
    }

    const set =
      prop.type === "assetRef"
        ? sharedSkinSet(
            data.map((values) => values[key]),
            assets,
          )
        : null;
    if (set) rows.push({ kind: "skin", key, label: SKIN_LABEL, set });
  }
  return rows;
}

/** The skin set of the model every entity holds, or null when they differ or it's in no set. */
function sharedSkinSet(values: readonly unknown[], assets: AssetLookup): SkinSet | null {
  const [first] = values;
  if (typeof first !== "string" || values.some((value) => value !== first)) return null;
  return assets.skinSetOf(first);
}

/** Whether a prop's `shownWhen` holds for one entity's component data. */
function isShown(
  prop: PropType,
  values: Readonly<Record<string, unknown>>,
  assets: AssetLookup,
): boolean {
  if (!prop.shownWhen) return true;
  const path = values[prop.shownWhen.assetProp];
  return typeof path === "string" && assets.kindOf(path) === prop.shownWhen.kind;
}
